"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";

interface SparklesLoadingProps {
  className?: string;
  isLoading: boolean;
}

// Sequence: rotate big sparkle (0-1s) → pause (1-1.4s) → pulse small sparkles (1.4-2.2s) → pause → repeat
const CYCLE = 2.8;

const REST = { duration: 0.3, ease: "easeOut" as const };

export function SparklesLoading({ className, isLoading }: SparklesLoadingProps) {
  const t = useTranslations("recipeChat");
  const prefersReduced = useReducedMotion();
  const active = isLoading && !prefersReduced;

  return (
    <span
      className="relative inline-flex items-center justify-center"
      aria-label={isLoading ? t("thinking") : undefined}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn(className, "overflow-visible")}
        aria-hidden
      >
        <motion.path
          d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
          style={{ transformOrigin: "12px 12px" }}
          initial={{ rotate: 0, opacity: 1 }}
          animate={
            active
              ? { rotate: [0, 360, 360, 360], opacity: [1, 1, 0.5, 1] }
              : { rotate: 0, opacity: 1 }
          }
          transition={
            active
              ? {
                  duration: CYCLE,
                  times: [0, 0.36, 0.6, 0.75],
                  repeat: Infinity,
                  ease: "easeInOut",
                }
              : REST
          }
        />

        <motion.g
          style={{ transformOrigin: "21px 5px" }}
          initial={{ opacity: 1, scale: 1 }}
          animate={
            active
              ? { opacity: [0.4, 0.4, 1, 0.4, 0.4], scale: [0.8, 0.8, 1.3, 0.8, 0.8] }
              : { opacity: 1, scale: 1 }
          }
          transition={
            active
              ? {
                  duration: CYCLE,
                  times: [0, 0.5, 0.64, 0.79, 1],
                  repeat: Infinity,
                  ease: "easeInOut",
                }
              : REST
          }
        >
          <path d="M20 3v4" />
          <path d="M22 5h-4" />
        </motion.g>

        <motion.g
          style={{ transformOrigin: "4px 18px" }}
          initial={{ opacity: 1, scale: 1 }}
          animate={
            active
              ? { opacity: [0.4, 0.4, 0.4, 1, 0.4], scale: [0.8, 0.8, 0.8, 1.3, 0.8] }
              : { opacity: 1, scale: 1 }
          }
          transition={
            active
              ? {
                  duration: CYCLE,
                  times: [0, 0.5, 0.57, 0.71, 0.86],
                  repeat: Infinity,
                  ease: "easeInOut",
                }
              : REST
          }
        >
          <path d="M4 17v2" />
          <path d="M5 18H3" />
        </motion.g>
      </svg>
    </span>
  );
}
